import _ from 'underscore';
import React from 'react';
import { browserHistory } from 'react-router';
import { connect } from 'react-redux';
import { routerActions } from 'react-router-redux';
import classNames from 'classnames';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import url from 'url.js';
import FeedUserTypes from 'common/feed-user-type';
import ProfileHeader from '../shared/profile-header';
import DeleteHighlightDialog from '../shared/delete-highlight-dialog/delete-highlight-dialog';
import FollowersDialog from '../shared/followers-dialog/followers-dialog';
import SharingDialog from 'components/shared/sharing-dialog/sharing-dialog';
import ImageCropperModal from 'components/shared/image-upload-modal/image-cropper-modal';
import ImageUploadModal from 'components/shared/image-upload-modal/image-upload-modal';
import ProfileBanner from 'components/shared/profile-banner';
import {
  beforeTeamBannerSave,
  beforeTeamProfileSave,
  closeDialog,
  openDialog,
  fetchProfileFollowers,
  followFeedUser,
  pageView,
  unfollowFeedUser,
  updateTeamImage,
  beginEditPictureUpdate,
  tempProfileImageUploaded,
  tempBannerImageUploaded,
} from 'common/actions';
import CustomizeProp from 'common/prop-types/customize';
import * as DialogTypes from 'common/dialog-types';
import PersonalizationUtility from 'utility/personalization-utility';
import AdTrackingUtil from 'utility/ad-tracking-utility';
import CommunityContentContainers from 'common/container-type';
import CommunityContentContainerTypes from 'common/container-subtype';
import CommunityContentSections from 'common/container-section';
import CommunityContentPlayers from 'common/container-player';
import DisplayAd from 'components/shared/display-ad';
import GoogleAdRefresher from '../../utility/google-ad-refresher';
import ads from '../../utility/ads';
import DisplayAdModel from '../../utility/ads/display-ad-model';
import ToastManager from 'components/shared/toast-manager';
import trackPage from '../../utility/page-tracking-utility';
import browserDetect from '../../utility/browser-detect';

class App extends React.Component {
  static propTypes = {
    customize: CustomizeProp.isRequired,
    forcePublicView: React.PropTypes.bool.isRequired,
    canDismissFeedItems: React.PropTypes.bool,
    presetFilterType: React.PropTypes.number,
    hideRosterAndSchedule: React.PropTypes.bool.isRequired,
    isMobile: React.PropTypes.bool,
    hasHighlightsDisabled: React.PropTypes.bool,
    feedItems: React.PropTypes.arrayOf(React.PropTypes.object),
    feedContentItems: React.PropTypes.arrayOf(React.PropTypes.object),
    team: React.PropTypes.shape({
      schoolName: React.PropTypes.string.isRequired,
      teamName: React.PropTypes.string.isRequired,
      highlightViewCount: React.PropTypes.number.isRequired,
      followCount: React.PropTypes.number.isRequired,
      teamId: React.PropTypes.string.isRequired,
      location: React.PropTypes.string,
      locationUrl: React.PropTypes.string,
      primaryColor: React.PropTypes.string,
      secondaryColor: React.PropTypes.string,
      sportName: React.PropTypes.string,
      teamLogoUri: React.PropTypes.string,
      mobileTeamLogoUri: React.PropTypes.string,
      teamCoverImage: React.PropTypes.string.isRequired,
      mobileTeamCoverImage: React.PropTypes.string.isRequired,
      tagline: React.PropTypes.string,
      keepsakes: React.PropTypes.shape({
        badPasswordEntered: React.PropTypes.bool,
        selectedGameId: React.PropTypes.string,
        isRequestingUrl: React.PropTypes.bool,
      }),
    }).isRequired,
    currentDialog: React.PropTypes.shape({
      dialogType: React.PropTypes.oneOf(_.values(DialogTypes)),
      data: React.PropTypes.object,
    }),
    followers: React.PropTypes.shape({
      oldestDateTime: React.PropTypes.instanceOf(Date),
    }).isRequired,
    dispatch: React.PropTypes.func.isRequired,
    user: React.PropTypes.object,
    seoFriendlyName: React.PropTypes.string.isRequired,
    subNavItems: React.PropTypes.arrayOf(React.PropTypes.shape({
      name: React.PropTypes.string.isRequired,
      url: React.PropTypes.string.isRequired,
    })),
    routing: React.PropTypes.shape({
      locationBeforeTransitions: React.PropTypes.shape({
        pathname: React.PropTypes.string.isRequired,
      }),
    }),
    children: React.PropTypes.oneOfType([
      React.PropTypes.element,
      React.PropTypes.arrayOf(React.PropTypes.element),
    ]),
    recruiterAccess: React.PropTypes.object,
    mediaSnowplowTrackerContext: React.PropTypes.object,
  }

  constructor() {
    super();
    this.state = {};
  }

  componentDidMount() {
    // Load first batch of followers
    this.props.dispatch(fetchProfileFollowers());
    if (this.props.user && this.props.user.isPrivilegedUser) {
      this.props.dispatch(pageView(this.props.user.userId, this.props.team.teamId));
    }

    // Make sure the navbar is showing when changing between tabs.
    // If going back to the timeline, restore the last scroll location.
    browserHistory.listen(location => {
      const header = document.querySelector('.prof-header');
      if (header && header.getBoundingClientRect) {
        const scrollArea = document.querySelector('.parallax');
        const rect = header.getBoundingClientRect();
        const navPosition = rect.bottom + scrollArea.scrollTop;
        const tab = this.getCurrentTab(location);

        if (tab === 'timeline' && this.timelinePosition && this.timelinePosition > navPosition) {
          scrollArea.scrollTop = this.timelinePosition;
        } else if (rect.bottom < 0) {
          scrollArea.scrollTop = navPosition;
        }
      }
    });

    if (this.props.team.teamId) {
      PersonalizationUtility.addRecentTeamId(this.props.team.teamId);
    }

    new GoogleAdRefresher([400, 700, 1000, 1055]); // eslint-disable-line no-new

    trackPage('TeamPage', this.props.mediaSnowplowTrackerContext);
  }

  getNavItems() {
    const navItems = [{
      key: 'profiles.tab_timeline',
      name: 'timeline',
    }];

    // Hide Videos in the navigation if feature privilege is present
    if (!this.props.hasHighlightsDisabled) {
      navItems.push({
        key: 'profiles.tab_highlights',
        name: 'highlights',
      });
    }

    // Show Games tab for all recruiters and tab decides content based on access level
    const isRecruiter = this.props.recruiterAccess &&
      (this.props.recruiterAccess.hasRecruiterAccess || this.props.recruiterAccess.canUpgradeForAccess);

    // If the team is in Recruit then the athletes/coaches should be able to preview Recruit content
    const isOnTeamInRecruit = this.props.user && this.props.user.isOnTeamInRecruit;

    if (isRecruiter || isOnTeamInRecruit) {
      navItems.push({
        key: 'profiles.tab_games',
        name: 'games',
        isRecruiterOnly: true,
      });
    }

    if (!this.props.hideRosterAndSchedule) {
      navItems.push(
        {
          key: 'profiles.tab_team',
          name: 'team',
        },
        {
          key: 'profiles.tab_schedule',
          name: 'schedule',
        }
      );
    }

    return navItems;
  }

  getDialog() {
    if (!this.props.currentDialog) {
      return undefined;
    }

    const dispatch = this.props.dispatch;

    switch (this.props.currentDialog.dialogType) {
    case DialogTypes.NONE:
      return undefined;
    case DialogTypes.DELETE_HIGHLIGHT_DIALOG:
      return (
        <DeleteHighlightDialog
          key="delete-highlight-dialog"
          dispatch={dispatch}
          onClose={() => dispatch(closeDialog(DialogTypes.DELETE_HIGHLIGHT_DIALOG))}
          {...this.props.currentDialog.data}
        />
      );
    case DialogTypes.FOLLOWERS_DIALOG:
      return (
        <FollowersDialog
          key="followers-dialog"
          title="Followers"
          followers={this.props.followers}
          addUserToModal={!!(this.props.user && this.props.user.isFollowing)}
          user={this.props.user}
          onClose={() => dispatch(closeDialog(DialogTypes.FOLLOWERS_DIALOG))}
          onScrollNearBottom={() => dispatch(fetchProfileFollowers(this.props.followers.oldestDateTime))}
          onFollowUnfollow={this.onFollowUnfollow.bind(this)}
        />
      );
    case DialogTypes.SHARE_PROFILE_DIALOG:
      return (
        <SharingDialog
          key="share-profile-dialog"
          onClose={() => dispatch(closeDialog(DialogTypes.SHARE_PROFILE_DIALOG))}
          user={this.props.user}
          pageUrl={window.location.origin + `/team/v2/${this.props.team.teamId}/${this.props.seoFriendlyName}`}
          twitterText={`Check out ${this.props.team.schoolName} - ${this.props.team.teamName} on @Hudl`}
          title="Share Profile"
          usageLogData={{
            OwnerId: this.props.team.teamId,
            Type: 'Team',
          }}
        />
      );
    case DialogTypes.SHARE_VIDEO_DIALOG:
      const dialogData = this.props.currentDialog.data;
      let schoolNamePossessive = this.props.team.schoolName;
      if (schoolNamePossessive[-1] === 's') {
        schoolNamePossessive += '\'';
      } else {
        schoolNamePossessive += '\'s';
      }

      return (
        <SharingDialog
          key="share-video-dialog"
          onClose={() => dispatch(closeDialog(DialogTypes.SHARE_VIDEO_DIALOG))}
          user={this.props.user}
          pageUrl={window.location.origin + dialogData.url}
          twitterText={dialogData.tweetText || `Check out ${schoolNamePossessive} ${dialogData.title} on @Hudl`}
          title="Share This Highlight"
          usageLogData={{
            OwnerId: this.props.team.teamId,
            UserId: this.props.user && this.props.user.userId,
            OwnerType: 'Team',
            Type: dialogData.type,
            Origin: 'TeamProfile',
            [(dialogData.type === 'VideoContent') ? 'ContentId' : 'ReelId']: dialogData.id,
          }}
        />
      );
    case DialogTypes.EDIT_PROFILE_IMAGE_DIALOG:
      return (
        <ImageUploadModal
          key="image"
          onClose={() => dispatch(closeDialog(DialogTypes.EDIT_PROFILE_IMAGE_DIALOG))}
          onImageUploaded={(imageSrc, imageId) => {
            dispatch(tempProfileImageUploaded(imageSrc, imageId));
            dispatch(openDialog(DialogTypes.EDIT_PROFILE_IMAGE_AREA_SELECT_DIALOG));
          }}
          title="Choose Your Team's Profile Picture"
          className="edit-team-profile-image-dialog"
          recommendedWidth={400}
          recommendedHeight={400}
          minWidth={180}
          minHeight={180}
          hideCroppedWarning
          isSquare
          errorMessage={this.state.errorMsg}
        />
      );
    case DialogTypes.EDIT_PROFILE_IMAGE_AREA_SELECT_DIALOG:
      return (
        <ImageCropperModal
          key="image-area"
          title="Crop Photo"
          className="edit-team-profile-image-dialog"
          recommendedWidth={400}
          recommendedHeight={400}
          minWidth={180}
          minHeight={180}
          onClose={() => dispatch(closeDialog(DialogTypes.EDIT_PROFILE_IMAGE_AREA_SELECT_DIALOG))}
          onChooseADifferentImage={() => dispatch(closeDialog(DialogTypes.EDIT_PROFILE_IMAGE_AREA_SELECT_DIALOG))}
          onBeforeSave={() => dispatch(beforeTeamProfileSave())}
          onSave={(args) => dispatch(updateTeamImage(args))}
          saveText="Save"
          imageSrc={this.props.customize.profileImage.tempValue}
          imageId={this.props.customize.profileImage.tempImageId}
          imageType="TEAM_LOGO"
          requesting={this.props.customize.profileImage.requesting}
          error={this.props.customize.profileImage.error}
          user={this.props.user}
          saveArgs={{
            teamId: this.props.team.teamId,
          }}
          isSquare
        />
      );
    case DialogTypes.EDIT_BANNER_IMAGE_AREA_SELECT_DIALOG:
      return (
        <ImageCropperModal
          key="banner-image-area"
          title="Crop Banner"
          className="edit-banner-image-dialog"
          recommendedWidth={1500}
          recommendedHeight={400}
          minWidth={1200}
          minHeight={340}
          onClose={() => dispatch(closeDialog(DialogTypes.EDIT_BANNER_IMAGE_AREA_SELECT_DIALOG))}
          onChooseADifferentImage={() => dispatch(closeDialog(DialogTypes.EDIT_BANNER_IMAGE_AREA_SELECT_DIALOG))}
          onBeforeSave={() => dispatch(beforeTeamBannerSave())}
          onSave={(args) => dispatch(updateTeamImage(args))}
          saveText="Save"
          imageSrc={this.props.customize.bannerImage.tempValue}
          imageId={this.props.customize.bannerImage.tempImageId}
          imageType="TEAM_PHOTO"
          requesting={this.props.customize.bannerImage.requesting}
          error={this.props.customize.bannerImage.error}
          user={this.props.user}
          saveArgs={{
            teamId: this.props.team.teamId,
            currentUserId: this.props.user.userId,
          }}
        />
      );
    case DialogTypes.EDIT_BANNER_IMAGE_DIALOG:
      return (
        <ImageUploadModal
          key="edit-banner"
          onClose={() => dispatch(closeDialog(DialogTypes.EDIT_BANNER_IMAGE_DIALOG))}
          onImageUploaded={(imageSrc, imageId) => {
            dispatch(tempBannerImageUploaded(imageSrc, imageId));
            dispatch(openDialog(DialogTypes.EDIT_BANNER_IMAGE_AREA_SELECT_DIALOG));
          }}
          title="Choose Your Team's Banner Picture"
          className="edit-banner-image-dialog"
          recommendedWidth={1500}
          recommendedHeight={400}
          minWidth={1200}
          minHeight={340}
          errorMessage={this.state.errorMsg}
        />
      );
    default:
      return undefined;
    }
  }

  onFollowUnfollow(feedUserTypeId, feedUserRelatedId, isFollow) {
    const isCurrentProfile = feedUserTypeId === 2 && feedUserRelatedId === this.props.team.teamId;

    const logData = {
      adTrackingId: AdTrackingUtil.getAdTrackingIdAndSetCookie(),
      container: CommunityContentContainers.Profile,
      containerType: CommunityContentContainerTypes.Team,
      containerSection: CommunityContentSections.Timeline,
      player: CommunityContentPlayers.Unknown,
    };

    if (isFollow) {
      this.props.dispatch(followFeedUser(feedUserTypeId, feedUserRelatedId, isCurrentProfile, logData));
    } else {
      this.props.dispatch(unfollowFeedUser(feedUserTypeId, feedUserRelatedId, isCurrentProfile, logData));
    }
  }

  getCurrentTab(location = this.props.routing.locationBeforeTransitions) {
    const path = location ? location.pathname : 'timeline';
    const indexOfLastSlash = path.lastIndexOf('/');
    const result = path.substring(indexOfLastSlash + 1);
    if (!['highlights', 'team', 'schedule', 'games'].some(s => s === result)) {
      return 'timeline';
    }
    return result;
  }

  togglePublicView() {
    const loc = url.parse(document.location.href, true);
    if (loc.get && loc.get.forcePublicView) {
      if (loc.get.forcePublicView !== 'false') {
        delete loc.get.forcePublicView;
      } else {
        loc.get.forcePublicView = 'true';
      }
    } else {
      loc.get = loc.get || {};
      loc.get.forcePublicView = 'true';
    }
    if (loc.query) {
      delete loc.query;
    }

    window.location = url.build(loc);
  }

  onChangeTab(name, changeWindowLocation, ignoreSeoFriendlyName) {
    const dispatch = this.props.dispatch;
    let newRoute;
    if (ignoreSeoFriendlyName) {
      newRoute = `/team/v2/${this.props.team.teamId}/${name}`;
    } else {
      newRoute = `/team/v2/${this.props.team.teamId}/${this.props.seoFriendlyName}/${name}`;
    }
    newRoute += document.location.search + document.location.hash;
    if (changeWindowLocation) {
      window.location = window.location.origin + newRoute;
    } else {
      const scrollArea = document.querySelector('.parallax');
      const currentTab = this.getCurrentTab();
      if (currentTab === 'timeline') {
        this.timelinePosition = scrollArea.scrollTop;
      }

      dispatch(routerActions.push(newRoute));
    }
  }

  onFollowersCountClick(e) {
    e.preventDefault();
    this.props.dispatch(openDialog(DialogTypes.FOLLOWERS_DIALOG));
    return false;
  }

  onProfileImageUpload(imageSrc, imageId) {
    const dispatch = this.props.dispatch;
    dispatch(tempProfileImageUploaded(imageSrc, imageId));
    dispatch(openDialog(DialogTypes.EDIT_PROFILE_IMAGE_AREA_SELECT_DIALOG));
  }

  onBannerImageUpload(imageSrc, imageId) {
    const dispatch = this.props.dispatch;
    dispatch(tempBannerImageUploaded(imageSrc, imageId));
    dispatch(openDialog(DialogTypes.EDIT_BANNER_IMAGE_AREA_SELECT_DIALOG));
  }

  beginEditOfPicture(imageType) {
    let authUser = undefined;
    if (this.props.user) {
      authUser = this.props.user.userId;
    }
    const dispatch = this.props.dispatch;
    dispatch(beginEditPictureUpdate(imageType, 'TeamPage', authUser, this.props.team.teamId, true));
  }

  onErrorPicture(errorMsg, isProfPicture) {
    this.setState({
      error: true,
      errorMsg,
    });
    if (isProfPicture) {
      this.props.dispatch(openDialog(DialogTypes.EDIT_PROFILE_IMAGE_DIALOG));
    } else {
      this.props.dispatch(openDialog(DialogTypes.EDIT_BANNER_IMAGE_DIALOG));
    }
  }

  getProfileHeaderName() {
    if (this.props.team.teamName && this.props.team.schoolName) {
      return this.props.team.schoolName + ' - ' + this.props.team.teamName;
    }
    return '';
  }

  renderMobileAd() {
    const currentTab = this.getCurrentTab();
    const adModel = {
      'team': new DisplayAdModel('div-gpt-ad-1455211124119-0', 'Display_TeamRoster_Mobile'),
      'highlights': new DisplayAdModel('div-gpt-ad-1455210563910-0', 'Display_TeamAllVideos_Mobile'),
      'schedule': new DisplayAdModel('div-gpt-ad-1455211433765-0', 'Display_TeamSchedule_Mobile'),
      'timeline': new DisplayAdModel('div-gpt-ad-1455210237052-0', 'Display_TeamTimeline_Mobile'),
    }[currentTab];

    return adModel && (
        <DisplayAd
        id={adModel.getId()}
        isMobile
        sizes={[ads.sizes.MobileLeaderboard]}
        path={adModel.getPath()}
        adSizeMapping={[ads.mappings.mobileLeaderboard, ads.mappings.leaderboard]}
      />
    );
  }

  hideLoadingState() {
    const feedLoading = document.getElementById('feed-loading-state');
    if (!feedLoading) {
      return;
    }
    const { feedItems, feedContentItems } = this.props;
    if ((feedItems && Object.keys(feedItems).length) || feedContentItems && Object.keys(feedContentItems).length) {
      feedLoading.style.display = 'none';
    }
  }

  render() {
    const isMobile = this.props.isMobile || browserDetect.isModernIPad;
    const onEditProfileClick = this.onChangeTab.bind(this, `edit/${this.props.seoFriendlyName}/customize`, true, true);
    const isLoggedIn = !!(this.props.user && this.props.user.userId);
    const onChangeTab = this.onChangeTab.bind(this);
    const classes = classNames({
      'mobile': isMobile,
      'loggedin': isLoggedIn,
      'prof-flex-height': true,
    });

    this.hideLoadingState();

    return (
      <div className={classes}>
        <div className="parallax-wrapper">
          <div className="parallax">
            <ProfileBanner
              beginEditOfPicture={(e) => this.beginEditOfPicture(e)}
              customize={this.props.customize}
              isLoggedIn={isLoggedIn}
              isMobile={this.props.isMobile}
              isPrivilegedUser={this.props.user && this.props.user.isPrivilegedUser}
              feedUserType={FeedUserTypes.Team}
              forcePublicView={this.props.forcePublicView}
              onChangeBannerImage={this.onBannerImageUpload.bind(this)}
              onErrorPicture={(errorMsg, isProfPic) => this.onErrorPicture(errorMsg, isProfPic)}
              profileBannerUri={this.props.team.teamCoverImage}
              mobileProfileBannerUri={this.props.team.mobileTeamCoverImage}
            />
            <ProfileHeader
              forcePublicView={this.props.forcePublicView}
              primaryName={this.getProfileHeaderName()}
              tagline={this.props.team.tagline}
              highlightViewCount={this.props.team.highlightViewCount}
              isPrivilegedUser={this.props.user && this.props.user.isPrivilegedUser}
              followCount={this.props.team.followCount}
              feedUserType={FeedUserTypes.Team}
              feedUserId={this.props.team.teamId}
              isMobile={this.props.isMobile}
              location={this.props.team.location}
              locationUrl={this.props.team.locationUrl}
              profileColor={this.props.team.primaryColor}
              profileLogoUri={this.props.team.teamLogoUri}
              mobileProfileLogoUri={this.props.team.mobileTeamLogoUri}
              secondaryColor={this.props.team.secondaryColor}
              sportName={this.props.team.sportName}
              user={this.props.user}
              onErrorPicture={(errorMsg, isProfPic) => this.onErrorPicture(errorMsg, isProfPic)}
              beginEditOfPicture={(e) => this.beginEditOfPicture(e)}
              onChangeBannerImage={this.onBannerImageUpload.bind(this)}
              onChangeProfileImage={this.onProfileImageUpload.bind(this)}
              onFollowersCountClick={(e) => this.onFollowersCountClick(e) }
              onShareButtonClick={() => this.props.dispatch(openDialog(DialogTypes.SHARE_PROFILE_DIALOG))}
              onPublicViewButtonClick={this.togglePublicView}
              onEditProfileClick={onEditProfileClick}
              onFollowUnfollow={this.onFollowUnfollow.bind(this)}
              currentTab={this.getCurrentTab()}
              onChangeTab={onChangeTab}
              navItems={this.getNavItems()}
              customize={this.props.customize}
            >
              {React.cloneElement(this.props.children, Object.assign({ onChangeTab: onChangeTab }, this.props))}
            </ProfileHeader>
          </div>
        </div>
        <ReactCSSTransitionGroup transitionName="react-modal" transitionEnterTimeout={500}
          transitionLeaveTimeout={300}>
          {this.getDialog()}
        </ReactCSSTransitionGroup>
        {isMobile && this.renderMobileAd()}
        <ToastManager />
      </div>
    );
  }
}

function select(state) {
  return state;
}

export default connect(select)(App);

